/**
 * @author funfly
 * @mail 389193@qq.com
 * @date 2025-11-13
 * @copyright Gridy.Art
 */
import utils from '@/js/utils'
import conf from '@/js/conf/conf'
import paletteConf from '@/js/conf/palettes'
import download from 'downloadjs'
import JSZip from 'jszip'
import i18n from '../../lang'
import { VOXLoader } from '@/js/three/VOXLoader.js'
import { trimVoxels, fillInnerVoxels, getVoxelHexColor } from '@/js/three/VOXUtils'
// eslint-disable-next-line
const $t = (key, value) => i18n.t(key, value)
const cdnHost = conf.hosts().cdnHost
const templatePath = cdnHost + 'template.3mf?' + conf.ver

// 体素类型配置
const VOXEL_TYPES = {
  BLOKY: 'bloky', // 默认类型：8mm × 8mm × 8mm
  CHAIN_BRICK: 'chain_brick', // 链条积木：7mm × 7mm × 5mm
  LEGO_BRICK: 'lego_brick' // 乐高积木：8mm × 8mm × 9.6mm
}

// 体素尺寸配置（单位：毫米）
const VOXEL_SIZES = {
  [VOXEL_TYPES.BLOKY]: { xy: 8, z: 8 },
  [VOXEL_TYPES.CHAIN_BRICK]: { xy: 7, z: 5 },
  [VOXEL_TYPES.LEGO_BRICK]: { xy: 8, z: 9.6 }
}

// 3MF 颜色索引代码数组（与 d3PaletteArr 一一对应）
const d3PaletteIdx = [
  '4', '8', '0C', '1C', '2C', '3C', '4C', '5C', '6C', '7C',
  '8C', '9C', 'AC', 'BC', 'CC', 'DC', 'EC', '0FC', '1FC', '2FC',
  '3FC', '4FC', '5FC', '6FC', '7FC', '8FC', '9FC', 'AFC', 'BFC',
  'CFC', 'DFC', 'EFC'
]

// 3MF 颜色调色板数组（从 paletteConf 中获取）
const d3PaletteArr = paletteConf.d3Arr || []

// 3D 打印模型尺寸限制常量
const MODEL_CONSTRAINTS = {
  MAX_DIMENSION: 200 // 最大尺寸（毫米）
}

const main = {
  /**
   * 将体素模型转换为 3MF 格式的压缩包文件并下载
   * @param {Blob} voxelBlob - 体素数据Blob（VOX格式）
   * @param {Function} cb - 回调函数 (error, fileName)
   * @param {string} type - 体素类型（可选，默认 'bloky'）
   * @param {number} scale - 缩放比例（可选，默认 0 表示自动计算）
   */
  run: async function(voxelBlob, cb, typeid = 4, name = '', scale = 0) {
    // 类型；0=百格画; 1=拼图; 2=图片; 3=3D模型; 4=体素模型; 5=串联积木模型; 6=乐高积木模型; 7=IP形象; 9=其他;
    let type = VOXEL_TYPES.BLOKY
    if (typeid === 5) {
      type = VOXEL_TYPES.CHAIN_BRICK
    } else if (typeid === 6) {
      type = VOXEL_TYPES.LEGO_BRICK
    }
    try {
      if (!voxelBlob) {
        return cb && cb($t('i.actFail'), null)
      }

      // 解析 VOX 文件
      const arrayBuffer = await voxelBlob.arrayBuffer()
      const loader = new VOXLoader()
      const chunks = loader.parse(arrayBuffer)

      if (!chunks || chunks.length === 0 || !chunks[0]) {
        return cb && cb($t('i.actFail'), null)
      }

      let chunk = chunks[0]
      if (!chunk.data || !chunk.size || !chunk.palette) {
        return cb && cb($t('i.actFail'), null)
      }

      // 裁剪空白并仅保留外壳体素，减少面片数量
      chunk = trimVoxels(chunk)
      // 填充模型内部的空腔，增强结构强度
      chunk = fillInnerVoxels(chunk)

      const size = chunk.size
      const data = chunk.data
      const palette = chunk.palette

      if (size.x <= 0 || size.y <= 0 || size.z <= 0) {
        return cb && cb($t('i.actFail'), null)
      }

      // 将体素数据转换为数组格式 [{x, y, z, i}, ...]
      const voxels = []
      for (let i = 0; i < data.length; i += 4) {
        const x = data[i]
        const y = data[i + 1]
        const z = data[i + 2]
        const c = data[i + 3]
        // 跳过透明体素（颜色索引为0）
        if (c === 0) continue
        voxels.push({ x, y, z, i: c })
      }

      if (voxels.length === 0) {
        return cb && cb($t('i.actFail'), null)
      }

      // 将 VOX 调色板颜色匹配到 3MF 调色板
      const d3Palette = this.getD3Palette(palette)

      // 体素转三角网格（只生成外露面）
      const meshData = this.convertVoxelsToMesh(voxels, size, d3Palette, type, scale)

      if (!meshData.vertices || meshData.vertices.length === 0) {
        return cb && cb($t('i.actFail'), null)
      }

      // 从网格数据中提取使用的颜色列表（meshData 中已包含 usedColors）
      const usedColors = meshData.usedColors || this.getUsedColorsFromTriangles(meshData.triangles)

      // 加载模板文件
      const templateBlob = await fetch(templatePath).then(res => {
        if (!res.ok) {
          cb && cb($t('i.actFail'), null)
          return
        }
        return res.blob()
      })
      const templateZip = await JSZip.loadAsync(templateBlob)

      // 从 zip 包中读取并解析项目配置文件
      let jsonConf = {}
      const configFile = templateZip.file('Metadata/project_settings.config')
      if (configFile) {
        const configContent = await configFile.async('string')
        if (configContent && configContent.trim()) {
          jsonConf = JSON.parse(configContent)
        }
      }

      // 创建项目配置（更新 jsonConf）
      const projectConfig = this.createProjectConfig(jsonConf, usedColors)
      templateZip.file('Metadata/project_settings.config', JSON.stringify(projectConfig, null, 4))

      // 生成 3MF XML（带颜色）
      const xml = this.generate3mfXml(meshData.vertices, meshData.triangles)
      templateZip.file('3D/Objects/object_14.model', xml)

      // 生成最终的 3MF 文件
      const finalBlob = await templateZip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 9 }
      })

      // 使用 download 下载文件
      const fileName = (name ? name + '.' : '') + 'Gridy.Art.3mf'
      download(finalBlob, fileName, 'application/vnd.ms-package.3dmanufacturing-3dmodel+xml')
      cb && cb(null, fileName)
    } catch (error) {
      cb && cb(error, null)
    }
  },

  /**
   * 将 VOX 调色板颜色匹配到 3MF 调色板
   * @param {Array<number>} rgbaValues - VOX 调色板数组（ARGB 格式，32 位整数）
   * @returns {Array<string>} 匹配后的调色板数组，每个元素是十六进制颜色值（如 '#FFFFFF'）
   */
  getD3Palette: function(rgbaValues) {
    if (!rgbaValues || !rgbaValues.length) return []
    const palette = new Array(rgbaValues.length).fill('')
    for (let i = 0; i < rgbaValues.length; i++) {
      if (rgbaValues[i]) {
        // 将ARGB格式转换为十六进制颜色
        const hexColor = getVoxelHexColor(rgbaValues[i])
        // 在3MF调色板中找到最匹配的颜色
        const matchedColor = utils.bestMatch(d3PaletteArr, hexColor)
        // 确保匹配的颜色有效
        if (matchedColor && matchedColor.trim() !== '') {
          palette[i] = matchedColor.toUpperCase() // 统一转换为大写
        } else {
          // 如果匹配失败，使用原始颜色（如果原始颜色在调色板中）
          const foundInPalette = d3PaletteArr.find(c => c.toUpperCase() === hexColor.toUpperCase())
          palette[i] = foundInPalette || hexColor.toUpperCase()
        }
      }
    }
    return palette
  },

  /**
   * 将体素转换为三角网格（只生成外露面）
   * @param {Array<{x: number, y: number, z: number, i: number}>} voxels - 体素数组
   * @param {Object} size - 模型尺寸 {x, y, z}
   * @param {Array<string>} d3Palette - 匹配后的3MF调色板数组
   * @param {string} type - 体素类型
   * @param {number} scale - 缩放比例
   * @returns {{vertices: Array, triangles: Array}} 网格数据
   */
  convertVoxelsToMesh: function(voxels, size, d3Palette, type, scale) {
    const vertices = []
    const triangles = []
    const vertexIndexMap = new Map()

    // 获取或创建顶点索引
    const getVertexIndex = (x, y, z) => {
      const key = `${x.toFixed(6)},${y.toFixed(6)},${z.toFixed(6)}`
      if (vertexIndexMap.has(key)) {
        return vertexIndexMap.get(key)
      }
      const idx = vertices.length
      vertices.push({ x, y, z })
      vertexIndexMap.set(key, idx)
      return idx
    }

    // 构建占用集合以快速判断邻居
    const occupied = new Set()
    for (let i = 0; i < voxels.length; i++) {
      const v = voxels[i]
      occupied.add(`${v.x},${v.y},${v.z}`)
    }

    // 六个方向定义：dx,dy,dz 以及该面的四个角
    const directions = [
      // -X 面（邻居在 x-1）
      { n: [-1, 0, 0], corners: (x, y, z) => [
        [x, y, z], [x, y + 1, z], [x, y, z + 1], [x, y + 1, z + 1]
      ] },
      // +X 面（邻居在 x+1）
      { n: [1, 0, 0], corners: (x, y, z) => [
        [x + 1, y, z], [x + 1, y, z + 1], [x + 1, y + 1, z], [x + 1, y + 1, z + 1]
      ] },
      // -Y 面（邻居在 y-1）
      { n: [0, -1, 0], corners: (x, y, z) => [
        [x, y, z], [x, y, z + 1], [x + 1, y, z], [x + 1, y, z + 1]
      ] },
      // +Y 面（邻居在 y+1）
      { n: [0, 1, 0], corners: (x, y, z) => [
        [x, y + 1, z], [x + 1, y + 1, z], [x, y + 1, z + 1], [x + 1, y + 1, z + 1]
      ] },
      // -Z 面（邻居在 z-1）
      { n: [0, 0, -1], corners: (x, y, z) => [
        [x, y, z], [x + 1, y, z], [x, y + 1, z], [x + 1, y + 1, z]
      ] },
      // +Z 面（邻居在 z+1）
      { n: [0, 0, 1], corners: (x, y, z) => [
        [x, y, z + 1], [x, y + 1, z + 1], [x + 1, y, z + 1], [x + 1, y + 1, z + 1]
      ] }
    ]

    // 根据体素类型获取尺寸配置
    const voxelSize = VOXEL_SIZES[type] || VOXEL_SIZES[VOXEL_TYPES.BLOKY]
    const voxelSizeXY = voxelSize.xy
    const voxelSizeZ = voxelSize.z

    // 计算模型实际尺寸（毫米）
    const sizeX = size.x * voxelSizeXY
    const sizeY = size.y * voxelSizeXY
    const sizeZ = size.z * voxelSizeZ

    // 计算缩放比例
    let scaleX, scaleY, scaleZ
    if (scale > 0) {
      // 使用指定的缩放比例
      scaleX = scale
      scaleY = scale
      scaleZ = scale * voxelSizeZ / voxelSizeXY // 保持 Z 轴比例
    } else {
      // 自动计算缩放，确保最大尺寸不超过 200mm
      const sizeMax = Math.max(sizeX, sizeY, sizeZ)
      let scaleRate = 1
      if (sizeMax > MODEL_CONSTRAINTS.MAX_DIMENSION) {
        scaleRate = MODEL_CONSTRAINTS.MAX_DIMENSION / sizeMax
      }
      scaleX = voxelSizeXY * scaleRate
      scaleY = voxelSizeXY * scaleRate
      scaleZ = voxelSizeZ * scaleRate
    }

    // 获取使用的颜色列表（用于 paint_color 映射）
    // 按首次出现的顺序收集使用的颜色
    const usedPaletteArr = []
    const usedPaletteObj = {}
    for (let i = 0; i < voxels.length; i++) {
      if (voxels[i].i) {
        const color = d3Palette[voxels[i].i]
        // 确保颜色有效且非空
        if (color && color.trim() !== '' && !usedPaletteObj[color]) {
          usedPaletteObj[color] = true
          usedPaletteArr.push(color)
        }
      }
    }

    // 如果没有使用的颜色，至少添加白色
    if (usedPaletteArr.length === 0) {
      usedPaletteArr.push('#FFFFFF')
    }

    // 获取 paint_color 的函数
    // 逻辑：体素颜色索引 -> d3Palette -> usedPaletteArr索引 -> d3PaletteIdx
    const getPaintColor = (c) => {
      if (!c || c === 0) return '4' // 默认白色

      // 确保颜色索引在有效范围内
      if (c < 0 || c >= d3Palette.length) {
        return '4'
      }

      // 从匹配后的3MF调色板获取颜色
      const color = d3Palette[c]
      if (!color || color.trim() === '') {
        // 如果调色板中该索引没有颜色，返回默认白色
        return '4'
      }

      // 统一转换为大写进行比较
      const colorUpper = color.toUpperCase()

      // 在使用的颜色列表中找到该颜色的索引
      let colorIndex = -1
      for (let i = 0; i < usedPaletteArr.length; i++) {
        if (usedPaletteArr[i].toUpperCase() === colorUpper) {
          colorIndex = i
          break
        }
      }

      if (colorIndex >= 0 && colorIndex < d3PaletteIdx.length) {
        // 使用该索引从 d3PaletteIdx 获取对应的 paint_color 代码
        return d3PaletteIdx[colorIndex] || '4'
      }

      // 如果找不到，返回默认白色
      return '4'
    }

    // 为每个体素生成外露面
    for (let i = 0; i < voxels.length; i++) {
      const v = voxels[i]
      const x = v.x
      const y = v.y
      const z = v.z
      const c = v.i || 0
      const paintColor = getPaintColor(c)

      for (let d = 0; d < directions.length; d++) {
        const dir = directions[d]
        const nx = x + dir.n[0]
        const ny = y + dir.n[1]
        const nz = z + dir.n[2]
        if (occupied.has(`${nx},${ny},${nz}`)) continue // 有邻居则内面，跳过

        const cs = dir.corners(x, y, z)
        // 应用缩放并转换为实际坐标（以中心为原点）
        const centerX = (size.x - 1) / 2
        const centerY = (size.y - 1) / 2
        const centerZ = (size.z - 1) / 2

        const i0 = getVertexIndex(
          (cs[0][0] - centerX) * scaleX,
          (cs[0][1] - centerY) * scaleY,
          (cs[0][2] - centerZ) * scaleZ
        )
        const i1 = getVertexIndex(
          (cs[1][0] - centerX) * scaleX,
          (cs[1][1] - centerY) * scaleY,
          (cs[1][2] - centerZ) * scaleZ
        )
        const i2 = getVertexIndex(
          (cs[2][0] - centerX) * scaleX,
          (cs[2][1] - centerY) * scaleY,
          (cs[2][2] - centerZ) * scaleZ
        )
        const i3 = getVertexIndex(
          (cs[3][0] - centerX) * scaleX,
          (cs[3][1] - centerY) * scaleY,
          (cs[3][2] - centerZ) * scaleZ
        )

        // 两个三角形，保证统一绕序
        triangles.push({ v1: i0, v2: i1, v3: i2, paint_color: paintColor })
        triangles.push({ v1: i2, v2: i1, v3: i3, paint_color: paintColor })
      }
    }

    // 返回网格数据和使用的颜色列表
    return {
      vertices,
      triangles,
      usedColors: usedPaletteArr // 直接返回使用的颜色列表，确保颜色信息准确
    }
  },

  /**
   * 从三角形数组中提取使用的颜色列表（按首次出现的顺序）
   */
  getUsedColorsFromTriangles: function(triangles) {
    const colorMap = new Map()
    const colorOrder = []
    for (const triangle of triangles) {
      if (triangle.paint_color) {
        const paintColor = triangle.paint_color
        if (!colorMap.has(paintColor)) {
          const colorIndex = d3PaletteIdx.indexOf(paintColor)
          if (colorIndex >= 0 && colorIndex < d3PaletteArr.length) {
            const hexColor = d3PaletteArr[colorIndex]
            colorMap.set(paintColor, hexColor)
            colorOrder.push(hexColor)
          }
        }
      }
    }

    // 如果没有颜色，至少添加白色
    if (colorOrder.length === 0) {
      colorOrder.push('#FFFFFF')
    }
    return colorOrder
  },

  /**
   * 生成 3MF 格式的 XML 文件内容
   */
  generate3mfXml: function(vertices, triangles) {
    const header = `<?xml version="1.0" encoding="UTF-8"?>\n` +
      `<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02" xmlns:BambuStudio="http://schemas.bambulab.com/package/2021" xmlns:p="http://schemas.microsoft.com/3dmanufacturing/production/2015/06" requiredextensions="p">\n` +
      ` <metadata name="BambuStudio:3mfVersion">1</metadata>\n` +
      ` <resources>\n` +
      `  <object id="1" p:UUID="000e0000-81cb-4c03-9d28-80fed5dfa1dc" type="model">\n` +
      `   <mesh>\n` +
      `    <vertices>\n`

    let verticesXml = ''
    for (let i = 0; i < vertices.length; i++) {
      const v = vertices[i]
      verticesXml += `     <vertex x="${v.x}" y="${v.y}" z="${v.z}"/>\n`
    }

    let trianglesXml = ''
    for (let i = 0; i < triangles.length; i++) {
      const t = triangles[i]
      const paintColor = t.paint_color || '4'
      const colorAttr = ` paint_color="${paintColor}"`
      trianglesXml += `     <triangle v1="${t.v1}" v2="${t.v2}" v3="${t.v3}"${colorAttr}/>\n`
    }

    const tail = verticesXml + `    </vertices>\n` +
      `    <triangles>\n` +
      trianglesXml +
      `    </triangles>\n` +
      `   </mesh>\n` +
      `  </object>\n` +
      ` </resources>\n` +
      ` <build>\n` +
      `  <item objectid="1"/>\n` +
      ` </build>\n` +
      `</model>\n`

    return header + tail
  },

  /**
   * 创建 3MF 项目配置文件
   * @param {Object} jsonConf - 从模板中读取的现有配置（如果存在）
   * @param {Array<string>} paletteArr - 使用的颜色数组（十六进制格式）
   * @returns {Object} 更新后的项目配置
   */
  createProjectConfig: function(jsonConf, paletteArr) {
    // 如果 jsonConf 为空或不是对象，初始化为空对象
    if (!jsonConf || typeof jsonConf !== 'object') {
      jsonConf = {}
    }
    const colorNums = paletteArr.length
    const defaultValue = {
      'activate_air_filtration': '0',
      'additional_cooling_fan_speed': '70',
      'chamber_temperatures': '0',
      'circle_compensation_speed': '200',
      'close_fan_the_first_x_layers': '1',
      'complete_print_exhaust_fan_speed': '70',
      'cool_plate_temp': '35',
      'cool_plate_temp_initial_layer': '35',
      'counter_coef_1': '0',
      'counter_coef_2': '0.008',
      'counter_coef_3': '-0.041',
      'counter_limit_max': '0.033',
      'counter_limit_min': '-0.035',
      'default_filament_colour': '',
      'diameter_limit': '50',
      'different_settings_to_system': '',
      'during_print_exhaust_fan_speed': '70',
      'enable_overhang_bridge_fan': '1',
      'enable_pressure_advance': '0',
      'eng_plate_temp': '0',
      'eng_plate_temp_initial_layer': '0',
      'fan_cooling_layer_time': '100',
      'fan_max_speed': '100',
      'fan_min_speed': '100',
      'filament_adaptive_volumetric_speed': ['0', '0'],
      'filament_adhesiveness_category': '100',
      'filament_change_length': '5',
      'filament_colour_type': '1',
      'filament_cost': '24.99',
      'filament_density': '1.26',
      'filament_deretraction_speed': ['nil', 'nil'],
      'filament_diameter': '1.75',
      'filament_end_gcode': '; filament end gcode \\n\\n',
      'filament_extruder_variant': ['Direct Drive Standard', 'Direct Drive High Flow'],
      'filament_flow_ratio': ['0.98', '0.98'],
      'filament_flush_temp': ['0', '0'],
      'filament_flush_volumetric_speed': ['0', '0'],
      'filament_ids': 'GFA00',
      'filament_is_support': '0',
      'filament_long_retractions_when_cut': ['1', '1'],
      'filament_map': '1',
      'filament_max_volumetric_speed': ['21', '21'],
      // 'filament_max_volumetric_speed': ['2', '2'],
      'filament_minimal_purge_on_wipe_tower': '15',
      'filament_pre_cooling_temperature': ['0', '0'],
      'filament_prime_volume': '30',
      'filament_printable': '3',
      'filament_ramming_travel_time': ['0', '0'],
      'filament_ramming_volumetric_speed': ['-1', '-1'],
      'filament_retract_before_wipe': ['nil', 'nil'],
      'filament_retract_restart_extra': ['nil', 'nil'],
      'filament_retract_when_changing_layer': ['nil', 'nil'],
      'filament_retraction_distances_when_cut': ['18', '18'],
      'filament_retraction_length': ['nil', 'nil'],
      'filament_retraction_minimum_travel': ['nil', 'nil'],
      'filament_retraction_speed': ['nil', 'nil'],
      'filament_scarf_gap': '0%',
      'filament_scarf_height': '10%',
      'filament_scarf_length': '10',
      'filament_scarf_seam_type': 'none',
      'filament_settings_id': 'Bambu PLA Basic @BBL X1C',
      // 'filament_settings_id': 'Bambu PLA Basic @BBL X1C 0.2 nozzle',
      'filament_shrink': '100%',
      'filament_soluble': '0',
      'filament_start_gcode': '; filament start gcode\\n{if  (bed_temperature[current_extruder] >55)||(bed_temperature_initial_layer[current_extruder] >55)}M106 P3 S200\\n{elsif(bed_temperature[current_extruder] >50)||(bed_temperature_initial_layer[current_extruder] >50)}M106 P3 S150\\n{elsif(bed_temperature[current_extruder] >45)||(bed_temperature_initial_layer[current_extruder] >45)}M106 P3 S50\\n{endif}\\nM142 P1 R35 S40\\n{if activate_air_filtration[current_extruder] && support_air_filtration}\\nM106 P3 S{during_print_exhaust_fan_speed_num[current_extruder]} \\n{endif}',
      // 'filament_start_gcode': '; filament start gcode\\n{if  (bed_temperature[current_extruder] >55)||(bed_temperature_initial_layer[current_extruder] >55)}M106 P3 S200\\n{elsif(bed_temperature[current_extruder] >50)||(bed_temperature_initial_layer[current_extruder] >50)}M106 P3 S150\\n{elsif(bed_temperature[current_extruder] >45)||(bed_temperature_initial_layer[current_extruder] >45)}M106 P3 S50\\n{endif}\\nM142 P1 R35 S40\\n{if activate_air_filtration[current_extruder] && support_air_filtration}\\nM106 P3 S{during_print_exhaust_fan_speed_num[current_extruder]} \\n{endif}',
      'filament_type': 'PLA',
      'filament_velocity_adaptation_factor': '1',
      'filament_vendor': 'Bambu Lab',
      'filament_wipe': ['nil', 'nil'],
      'filament_wipe_distance': ['nil', 'nil'],
      'filament_z_hop': ['nil', 'nil'],
      'filament_z_hop_types': ['nil', 'nil'],
      'first_x_layer_fan_speed': '0',
      // 该参数暂时无法计算
      'flush_volumes_matrix': ['0', '0'],
      'flush_volumes_vector': ['140', '140'],
      'full_fan_speed_layer': '0',
      'hole_coef_1': '0',
      'hole_coef_2': '-0.008',
      'hole_coef_3': '0.23415',
      'hole_limit_max': '0.22',
      'hole_limit_min': '0.088',
      'hot_plate_temp': '55',
      'hot_plate_temp_initial_layer': '55',
      'impact_strength_z': '13.8',
      'long_retractions_when_ec': ['0', '0'],
      'no_slow_down_for_cooling_on_outwalls': '0',
      'nozzle_temperature': ['220', '220'],
      'nozzle_temperature_initial_layer': ['220', '220'],
      'nozzle_temperature_range_high': '240',
      'nozzle_temperature_range_low': '190',
      'overhang_fan_speed': '100',
      'overhang_fan_threshold': '50%',
      'overhang_threshold_participating_cooling': '95%',
      'pre_start_fan_time': '0',
      'pressure_advance': '0.02',
      'reduce_fan_stop_start_freq': '1',
      'required_nozzle_HRC': '3',
      'retraction_distances_when_ec': ['0', '0'],
      'slow_down_for_layer_cooling': '1',
      'slow_down_layer_time': '4',
      'slow_down_min_speed': '20',
      'supertack_plate_temp': '45',
      'supertack_plate_temp_initial_layer': '45',
      'temperature_vitrification': '45',
      'textured_plate_temp': '55',
      'textured_plate_temp_initial_layer': '55',
      'volumetric_speed_coefficients': ['0 0 0 0 0 0', '0 0 0 0 0 0']
    }

    // 根据 defaultValue 生成/覆盖对应 key 的数组值
    const buildArray = function(val, len) {
      if (Array.isArray(val)) {
        const out = new Array(len)
        const base = val.length > 0 ? val : ['']
        for (let i = 0; i < len; i++) out[i] = base[i % base.length]
        return out
      }
      const out = new Array(len)
      for (let i = 0; i < len; i++) out[i] = val
      return out
    }

    // 更新配置：如果 jsonConf 中已有该 key，保留原有值；否则使用默认值
    Object.keys(defaultValue).forEach((key) => {
      const defVal = defaultValue[key]
      const isArray = Array.isArray(defVal)
      const targetLen = isArray ? colorNums * defVal.length : colorNums

      // 如果 jsonConf 中已有该 key，检查是否需要扩展数组长度
      if (Object.prototype.hasOwnProperty.call(jsonConf, key)) {
        const existingVal = jsonConf[key]
        if (Array.isArray(existingVal)) {
          // 如果现有值是数组，检查长度是否匹配
          if (existingVal.length !== targetLen) {
            // 长度不匹配，使用默认值重新构建
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果长度匹配，保留原有值
        } else {
          // 如果现有值不是数组，但默认值需要数组，则使用默认值构建
          if (isArray || targetLen > 1) {
            jsonConf[key] = buildArray(defVal, targetLen)
          }
          // 如果都是标量，保留原有值
        }
      } else {
        // 如果 jsonConf 中没有该 key，使用默认值构建
        jsonConf[key] = buildArray(defVal, targetLen)
      }
    })

    jsonConf.filament_colour = paletteArr
    jsonConf.filament_multi_colour = paletteArr
    jsonConf.different_settings_to_system = jsonConf.different_settings_to_system || []
    jsonConf.different_settings_to_system.unshift('enable_support')

    const filament_self_index = []
    for (let i = 1; i <= colorNums; i++) {
      filament_self_index.push(i.toString())
      filament_self_index.push(i.toString())
    }
    jsonConf.filament_self_index = filament_self_index

    return jsonConf
  }
}
export default main
